import type { PaginationMeta, SuccessResponse } from "@shared/types";
import apiClient from "@/app/api/client";
import { endpoints } from "@/shared/api/endpoints";
import type { ClientDetail, ClientStatus } from "../types/clients.types";

export type BackendClient = {
	id: string;
	firstName: string;
	lastName: string;
	surname: string | null;
	birthDate: string | null;
	sex: string | null;
	phoneNumber: string;
	phoneNumber2: string | null;
	phoneNumber3: string | null;
	address: string | null;
	region: string | null;
	district: string | null;
	passportNumber: string | null;
	jshshir: string | null;
	passportGivenDate: string | null;
	passportGivenBy: string | null;
	passportFrontImage: string | null;
	passportBackImage: string | null;
	status: string;
	createdAt: string;
};

export type BackendClientListItem = {
	id: string;
	fullName: string;
	firstName: string;
	lastName: string;
	surname: string | null;
	phoneNumber: string;
	buildingName: null | string;
	roomNumber: null | string;
	floor: null | number;
	paymentType: null | string;
	paidPercentage: number;
	status: string;
	debtsCount: number;
	manager: null | {
		id: string;
		fullName: string;
	};
	createdAt: null | string;
};

export type Client = ClientDetail;

const tones: ClientDetail["tone"][] = ["blue", "green", "violet", "amber", "teal"];

function getInitials(firstName: string, lastName: string) {
	return `${firstName[0] || ""}${lastName[0] || ""}`.toUpperCase();
}

function normalizeClientStatus(status: string): ClientStatus {
	if (
		status === "active" ||
		status === "disabled" ||
		status === "faol" ||
		status === "bekor" ||
		status === "bekorqilingan"
	) {
		return status;
	}

	return "active";
}

function normalizePaidPercentage(value: number) {
	if (!Number.isFinite(value)) {
		return 0;
	}

	return Math.max(0, Math.min(100, value));
}

function mapClient(data: BackendClient): ClientDetail {
	const toneIndex =
		data.id.split("").reduce((sum, char) => sum + char.charCodeAt(0), 0) % tones.length;

	return {
		id: data.id,
		firstName: data.firstName,
		lastName: data.lastName,
		patronymic: data.surname || "",
		initials: getInitials(data.firstName, data.lastName),
		tone: tones[toneIndex] || "blue",
		phone: data.phoneNumber,
		phoneAdd1: data.phoneNumber2 || undefined,
		phoneAdd2: data.phoneNumber3 || undefined,
		gender: (data.sex as "erkak" | "ayol") || "erkak",
		birthYear: data.birthDate ? new Date(data.birthDate).getFullYear() : undefined,
		birthDate: data.birthDate || undefined,
		region: data.region || "",
		district: data.district || "",
		address: data.address || "",
		buildingName: "-",
		roomNumber: "-",
		paymentType: "naqd",
		paidPercent: 0,
		debtCount: 0,
		managerName: "-",
		status: normalizeClientStatus(data.status),
		passport: {
			seriesNumber: data.passportNumber || "",
			jshshir: data.jshshir || "",
			issueDate: data.passportGivenDate || "",
			issuedBy: data.passportGivenBy || "",
			passportFrontImage: data.passportFrontImage || "",
			passportBackImage: data.passportBackImage || "",
		},
		schedule: [],
		salesHistory: [],
		smsHistory: [],
	};
}

function mapClientListItem(data: BackendClientListItem): ClientDetail {
	const toneIndex =
		data.id.split("").reduce((sum, char) => sum + char.charCodeAt(0), 0) % tones.length;
	const { firstName, lastName } = data;
	const patronymic = data.surname || "";

	return {
		id: data.id,
		firstName,
		lastName,
		patronymic,
		initials: getInitials(firstName, lastName),
		tone: tones[toneIndex] || "blue",
		phone: data.phoneNumber,
		phoneAdd1: undefined,
		phoneAdd2: undefined,
		gender: "erkak",
		birthYear: undefined,
		birthDate: undefined,
		region: "",
		district: "",
		address: "",
		buildingName: data.buildingName || "-",
		roomNumber: data.roomNumber || "-",
		paymentType: data.paymentType === "muddatli" ? "muddatli" : "naqd",
		paidPercent: normalizePaidPercentage(data.paidPercentage),
		debtCount: data.debtsCount,
		managerName: data.manager?.fullName || "-",
		status: normalizeClientStatus(data.status),
		passport: {
			seriesNumber: "",
			jshshir: "",
			issueDate: "",
			issuedBy: "",
			passportFrontImage: "",
			passportBackImage: "",
		},
		schedule: [],
		salesHistory: [],
		smsHistory: [],
	};
}

export type ClientsListParams = {
	search?: string;
	status?: string;
	managerId?: string;
	limit?: number;
	page?: number;
};

type BackendClientsListResponse =
	| {
			data: BackendClientListItem[];
			meta: PaginationMeta;
	  }
	| SuccessResponse<{
			items: BackendClientListItem[];
			meta: PaginationMeta;
	  }>;

function extractClientsList(responseData: BackendClientsListResponse) {
	if ("success" in responseData) {
		return responseData.data;
	}

	return {
		items: responseData.data,
		meta: responseData.meta,
	};
}

export async function getClients(params?: ClientsListParams) {
	const response = await apiClient.get<BackendClientsListResponse>(endpoints.clients.list, {
		params,
	});
	const list = extractClientsList(response.data);
	const items = list.items.map(mapClientListItem);
	return { items, meta: list.meta };
}

export async function getClientById(id: string) {
	const response = await apiClient.get<SuccessResponse<BackendClient>>(
		endpoints.clients.detail(id)
	);
	return mapClient(response.data.data);
}

export type ClientSavePayload = {
	id?: string;
	firstName: string;
	lastName: string;
	patronymic?: string;
	birthDate?: string;
	gender?: "erkak" | "ayol";
	phone: string;
	phoneAdd1?: string;
	phoneAdd2?: string;
	region?: string;
	district?: string;
	address?: string;
	passport?: {
		seriesNumber?: string;
		jshshir?: string;
		issueDate?: string;
		issuedBy?: string;
		passportFrontImage?: string;
		passportBackImage?: string;
	};
};

export async function submitClientSave(payload: ClientSavePayload) {
	const passportFrontImage =
		payload.passport && "passportFrontImage" in payload.passport
			? payload.passport.passportFrontImage
			: undefined;
	const passportBackImage =
		payload.passport && "passportBackImage" in payload.passport
			? payload.passport.passportBackImage
			: undefined;

	const backendPayload = {
		firstName: payload.firstName,
		lastName: payload.lastName,
		surname: payload.patronymic || undefined,
		birthDate: payload.birthDate || undefined,
		sex: payload.gender || undefined,
		phoneNumber: payload.phone,
		phoneNumber2: payload.phoneAdd1 || undefined,
		phoneNumber3: payload.phoneAdd2 || undefined,
		region: payload.region || undefined,
		district: payload.district || undefined,
		address: payload.address || undefined,
		passportNumber: payload.passport?.seriesNumber || undefined,
		jshshir: payload.passport?.jshshir || undefined,
		passportGivenDate: payload.passport?.issueDate || undefined,
		passportGivenBy: payload.passport?.issuedBy || undefined,
		passportFrontImage,
		passportBackImage,
	};

	if (payload.id) {
		const response = await apiClient.patch<SuccessResponse<BackendClient>>(
			endpoints.clients.update(payload.id),
			backendPayload
		);
		return mapClient(response.data.data);
	} else {
		const response = await apiClient.post<SuccessResponse<BackendClient>>(
			endpoints.clients.create,
			backendPayload
		);
		return mapClient(response.data.data);
	}
}

export async function deleteClient(clientId: string): Promise<void> {
	await apiClient.delete<void>(endpoints.clients.detail(clientId));
}
